import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Alert, Modal } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaCertificate, FaDownload } from 'react-icons/fa';
import TrainingFormSection from './TrainingFormSection';
import { TRAINING_LIST, TRAINING_MODES, TRAINING_STATUSES, YEARS } from '../../../../Constants/constants';
import axios from 'axios';
import { BASE_URL } from '../../../../Constants/constants';
import { useAuth } from '../../../AuthContext/AuthContext';
import { useNotification } from '../../../SharedComponents/Notifications/NotificationContext';
import {
    validateTrainingFormData,
    sanitizePayload,
    uploadTrainingCertificate,
    deleteTrainingCertificate,
    downloadTrainingCertificate
} from './TrainingActions';
import { useTranslation } from 'react-i18next';

const TrainingForm = ({ onSave, onValidationChange }) => {
    const { t } = useTranslation('training');
    const { currentUser } = useAuth();
    const { showNotification } = useNotification();

    const [trainings, setTrainings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        training_name: '',
        training_status: '',
        training_mode: '',
        year_completed: '',
        training_certificate: null
    });
    const [formErrors, setFormErrors] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        fetchTrainings();
    }, []);

    const fetchTrainings = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(`${BASE_URL}/training/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            let trainingData = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    trainingData = response.data;
                } else if (typeof response.data === 'object') {
                    trainingData = [response.data];
                }
            }

            // Fetch certificate for each training
            const trainingsWithCertificate = await Promise.all(
                trainingData.map(async (training) => {
                    try {
                        const certResponse = await axios.get(
                            `${BASE_URL}/training/${training.id}/certificate`,
                            { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
                        );
                        return {
                            ...training,
                            training_certificate: certResponse.data || null
                        };
                    } catch (error) {
                        // If certificate endpoint fails, just return training without certificate
                        return {
                            ...training,
                            training_certificate: null
                        };
                    }
                })
            );

            setTrainings(trainingsWithCertificate);

            if (onValidationChange) {
                console.log(t('operations.updatingParentWithData'), trainingsWithCertificate);
                onValidationChange(trainingsWithCertificate);
            }
        } catch (error) {
            console.error(t('operations.errorFetchingTraining'), error);
            if (error.response && error.response.status === 404) {
                setTrainings([]);
                if (onValidationChange) {
                    onValidationChange([]);
                }
            } else {
                setError(t('error.failedLoad'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (training) => {
        setFormData({
            ...training,
            training_name: training.training_name || '',
            training_status: training.training_status || '',
            training_mode: training.training_mode || '',
            year_completed: training.year_completed || '',
            training_certificate: training.training_certificate || null
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleAdd = () => {
        setFormData({
            training_name: '',
            training_status: '',
            training_mode: '',
            year_completed: '',
            training_certificate: null
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // If training_status is being changed and it's not "Completed", clear the certificate and year_completed
        if (name === 'training_status' && value !== 'Completed') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                training_certificate: null,
                year_completed: ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleCertificateUpload = (certificate) => {
        setFormData(prev => ({
            ...prev,
            training_certificate: certificate
        }));
    };

    const handleCertificateDelete = async () => {
        const certificate = formData.training_certificate;

        // If it's a saved certificate, delete it from server
        if (certificate && certificate.id && formData.id) {
            try {
                await deleteTrainingCertificate(formData.id, certificate.id);
                showNotification("success", t('notifications.certificateDeleted'));
            } catch (error) {
                showNotification("error", t('error.failedDeleteCertificate'));
                return;
            }
        }

        setFormData(prev => ({
            ...prev,
            training_certificate: null
        }));
    };

    const handleCertificateDownload = async (training) => {
        const certificate = training.training_certificate;
        if (certificate && certificate.id && training.id) {
            try {
                await downloadTrainingCertificate(
                    training.id,
                    certificate.id,
                    certificate.filename || certificate.original_name || `${training.training_name}_certificate`
                );
            } catch (error) {
                showNotification("error", t('error.failedDownloadCertificate'));
            }
        }
    };

    const handleSave = async () => {
        const validationErrors = validateTrainingFormData(formData);
        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            return;
        }

        setIsUploading(true);

        try {
            const token = localStorage.getItem('accessToken');
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            };

            const trainingData = {
                ...formData,
                user_id: currentUser?.id
            };

            const sanitizedData = sanitizePayload(trainingData);
            let savedTraining;

            // Save training record first
            if (formData.id) {
                const response = await axios.put(`${BASE_URL}/training/${formData.id}`, sanitizedData, config);
                savedTraining = response.data;
            } else {
                const response = await axios.post(`${BASE_URL}/training`, sanitizedData, config);
                savedTraining = response.data;
            }

            // Upload new certificate if it's a File object
            if (formData.training_certificate instanceof File) {
                try {
                    const uploadedCertificate = await uploadTrainingCertificate(savedTraining.id, formData.training_certificate);
                    savedTraining.training_certificate = uploadedCertificate;
                } catch (uploadError) {
                    console.error('Certificate upload failed:', uploadError);
                    showNotification("warning", t('notifications.trainingSavedCertificateUploadFailed'));
                }
            } else {
                // Keep existing certificate if no new one was added
                savedTraining.training_certificate = formData.training_certificate;
            }

            // Update local state
            const updatedTrainings = formData.id
                ? trainings.map(item => item.id === formData.id ? savedTraining : item)
                : [...trainings, savedTraining];

            console.log(t('operations.updatedTrainingsAfterSave'), updatedTrainings);
            setTrainings(updatedTrainings);

            // Notify parent component of update
            if (onSave) {
                console.log(t('operations.callingOnSaveWithUpdated'));
                onSave('training', updatedTrainings);
            }

            if (onValidationChange) {
                console.log(t('operations.callingOnValidationChangeWithUpdated'));
                onValidationChange(updatedTrainings);
            }

            showNotification("success", t('notifications.saved'));
            setShowModal(false);
        } catch (error) {
            console.error(t('operations.failedToSaveTraining'), error);
            setError(t('error.failedSave'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (training) => {
        try {
            console.log(t('operations.startingDeletion'), training.id);
            const token = localStorage.getItem('accessToken');

            // Delete certificate first if it exists
            if (training.training_certificate && training.training_certificate.id) {
                await deleteTrainingCertificate(training.id, training.training_certificate.id);
            }

            // Delete the training record
            const response = await axios.delete(`${BASE_URL}/training/${training.id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                data: {
                    id: training.id,
                    user_id: currentUser.id,
                    confirmation: true
                }
            });

            console.log(t('operations.deleteResponse'), response);

            // Update local state
            const updatedTrainings = trainings.filter(item => item.id !== training.id);
            console.log(t('operations.updatedTrainingsAfterDelete'), updatedTrainings);
            setTrainings(updatedTrainings);

            // Notify parent component of update
            if (onSave) {
                console.log(t('operations.callingOnSaveAfterDeletion'), updatedTrainings);
                onSave('training', updatedTrainings);
            }

            if (onValidationChange) {
                console.log(t('operations.callingOnValidationChangeAfterDeletion'), updatedTrainings);
                onValidationChange(updatedTrainings);
            }

            showNotification("success", t('notifications.deleted'));
        } catch (error) {
            console.error(t('operations.failedToDeleteTraining'), error);
            console.error(t('operations.errorDetails'), error.response?.data || error.message);
            setError(t('error.failedDelete'));
        }
    };

    // Check if training has a certificate
    const hasCertificate = (training) => {
        return training.training_certificate && training.training_certificate.id;
    };

    return (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">{t('nameLabel')}</h5>
                <Button variant="primary" onClick={handleAdd}>
                    <FaPlus className="me-2" /> {t('addButton')}
                </Button>
            </div>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            <Table responsive hover>
                <thead>
                <tr>
                    <th>{t('table.name')}</th>
                    <th>{t('table.status')}</th>
                    <th>{t('table.mode')}</th>
                    <th>{t('table.year')}</th>
                    <th>{t('table.certificate')}</th>
                    <th>{t('table.actions')}</th>
                </tr>
                </thead>
                <tbody>
                {isLoading ? (
                    <tr>
                        <td colSpan="6" className="text-center py-3">{t('table.loading')}</td>
                    </tr>
                ) : trainings.length === 0 ? (
                    <tr>
                        <td colSpan="6" className="text-center py-3">{t('table.noRecords')}</td>
                    </tr>
                ) : (
                    trainings.map((training, index) => (
                        <tr key={index}>
                            <td>{training.training_name || t('table.n_a')}</td>
                            <td>{training.training_status || t('table.n_a')}</td>
                            <td>{training.training_mode || t('table.n_a')}</td>
                            <td>{training.year_completed || t('table.n_a')}</td>
                            <td>
                                {hasCertificate(training) ? (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 d-flex align-items-center"
                                        onClick={() => handleCertificateDownload(training)}
                                        title={t('certificates.downloadCertificate')}
                                    >
                                        <FaCertificate className="me-2 text-primary" />
                                        <span className="text-primary">{t('certificates.downloadLink')}</span>
                                    </Button>
                                ) : (
                                    <span className="text-muted">{t('certificates.noCertificate')}</span>
                                )}
                            </td>
                            <td>
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => handleEdit(training)}
                                    className="p-0 me-3"
                                >
                                    <FaEdit title={t('editButton')} />
                                </Button>
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => handleDelete(training)}
                                    className="p-0 text-danger"
                                >
                                    <FaTrash title={t('deleteButton')} />
                                </Button>
                            </td>
                        </tr>
                    ))
                )}
                </tbody>
            </Table>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {formData.id ? t('modal.editTitle') : t('modal.addTitle')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <TrainingFormSection
                        formData={formData}
                        handleChange={handleInputChange}
                        errors={formErrors}
                        onCertificateUpload={handleCertificateUpload}
                        onCertificateDelete={handleCertificateDelete}
                        onCertificateDownload={() => {
                            if (formData.training_certificate instanceof File) {
                                const url = URL.createObjectURL(formData.training_certificate);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = formData.training_certificate.name;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                            }
                        }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        {t('modal.cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={isUploading}
                    >
                        {isUploading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t('modal.uploading')}
                            </>
                        ) : (
                            formData.id ? t('editButton') : t('addButton')
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TrainingForm;